import * as React from "react";
import dayjs from "dayjs";

import { Box, Grid, Typography } from "@mui/material";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomButton from "../../../../helper-components/input/CustomButton";

import { validateRow } from "../../../../helper-functions/validation/objectValidation";
import { addFormToObj, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { IS_LIBRARY_VALID, LIBRARY_VALIDATION } from "../../../../helper-functions/validation/libraryValidation";

import { getLanguage } from "../../../../config/language/language";
import { LIBRARY_REQUEST_OBJECT } from "../../../../config/constants/Constants";

/**
 * A form to update a library.
 */
export default function LibraryEditForm(props) {
    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = LIBRARY_REQUEST_OBJECT;
    const validation = LIBRARY_VALIDATION;
    const isValid = IS_LIBRARY_VALID;

    const savedTypes = Array.isArray(existingRow.config?.ingest_video_types) ? existingRow.config.ingest_video_types : [];
    const keepAllTypes = savedTypes.length === 0;

    const [row, setRow] = React.useState(addFormToObj({
        ...JSON.parse(JSON.stringify(object)),
        config_send_webhook: Boolean(existingRow.config?.send_webhook),
        config_llm_cleanup: Boolean(existingRow.config?.llm_cleanup),
        config_llm_cleanup_auto_only: Boolean(existingRow.config?.llm_cleanup_auto_only),
        config_save_videos: keepAllTypes || savedTypes.includes("videos"),
        config_save_shorts: keepAllTypes || savedTypes.includes("shorts"),
        config_save_streams: keepAllTypes || savedTypes.includes("streams")
    }));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            // Convert to dayjs format if datetime exists.
            created_on: existingRow.created_on ? dayjs(existingRow.created_on) : null,
            updated_on: existingRow.updated_on ? dayjs(existingRow.updated_on) : null,

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            },

            config_send_webhook: Boolean(existingRow.config?.send_webhook),
            config_llm_cleanup: Boolean(existingRow.config?.llm_cleanup),
            config_llm_cleanup_auto_only: Boolean(existingRow.config?.llm_cleanup_auto_only),
            config_save_videos: keepAllTypes || savedTypes.includes("videos"),
            config_save_shorts: keepAllTypes || savedTypes.includes("shorts"),
            config_save_streams: keepAllTypes || savedTypes.includes("streams")
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newLibrary = JSON.parse(JSON.stringify(row));
        setValue(newLibrary, key, value);

        validateRow(obj, validation, newLibrary);
        setInsertObjectState(setRow, newLibrary);
    };

    const handleClick = () => {
        if (!isValid(row)) {
            props.showSnackbar({ message: getLanguage().SNACKBAR_ERROR_FORM_INVALID, color: "error" });
            return;
        }

        const chosenTypes = [
            row.config_save_videos ? "videos" : null,
            row.config_save_shorts ? "shorts" : null,
            row.config_save_streams ? "streams" : null
        ].filter(Boolean);

        if (chosenTypes.length === 0) {
            props.showSnackbar({ message: getLanguage().SNACKBAR_ERROR_VIDEO_TYPE_REQUIRED, color: "error" });
            return;
        }

        const sourceConfig = existingRow.config && typeof existingRow.config === "object" ? existingRow.config : {};
        const config = {
            ...sourceConfig,
            send_webhook: Boolean(row.config_send_webhook),
            llm_cleanup: Boolean(row.config_llm_cleanup),
            llm_cleanup_auto_only: Boolean(row.config_llm_cleanup_auto_only),
            ingest_video_types: chosenTypes.length === 3 ? [] : chosenTypes
        };

        callback(true, { row: { ...row, config } });
    };

    const switchField = (name, label, help) => (
        <Grid item xs={12}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                <CustomInput name={name} row={row} onChange={onChange} label={label} switch />
                {help ? (
                    <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 0.5, maxWidth: 560 }}>
                        {help}
                    </Typography>
                ) : null}
            </Box>
        </Grid>
    );

    return (
        <Box className="box">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <CustomInput name="name" row={row} onChange={onChange} label="Name" />
                </Grid>

                <Grid item xs={12}>
                    <CustomInput name="description" row={row} onChange={onChange} label="Description" multiline minRows={2} maxRows={5} />
                </Grid>

                <Grid item xs={12}>
                    <CustomInput name="url" row={row} onChange={onChange} label="URL" />
                </Grid>

                {switchField("config_send_webhook", getLanguage().LIBRARY_CONFIG_WEBHOOK, getLanguage().LIBRARY_CONFIG_WEBHOOK_HELP)}
                {switchField("config_llm_cleanup", getLanguage().LIBRARY_CONFIG_CLEANUP, getLanguage().LIBRARY_CONFIG_CLEANUP_HELP)}
                {switchField("config_llm_cleanup_auto_only", getLanguage().LIBRARY_CONFIG_CLEANUP_AUTO_ONLY, getLanguage().LIBRARY_CONFIG_CLEANUP_AUTO_ONLY_HELP)}
                {switchField("config_save_videos", getLanguage().LIBRARY_CONFIG_VIDEOS, getLanguage().LIBRARY_CONFIG_TYPES_HELP)}
                {switchField("config_save_shorts", getLanguage().LIBRARY_CONFIG_SHORTS)}
                {switchField("config_save_streams", getLanguage().LIBRARY_CONFIG_STREAMS)}
            </Grid>

            <CustomButton {...props} title={"Update library"} onClick={handleClick} className="button" fullWidth />
        </Box>
    );
}
